import classNames from 'classnames';
import React from 'react';
import { MdHub } from 'react-icons/md';
import { Icon as RIcon, Tooltip, Whisper } from 'rsuite';
import Table, { TableProps } from 'rsuite/lib/Table';
import { TablePaginationProps } from 'rsuite/lib/Table/TablePagination';
import { SecondaryButton, TableBadge } from '../../../../../../../components';
import { parseStringToPlural } from '../../../../../../../utils';
import { formatDateToPtBr } from '../../../../../../../utils/date';
import { GetNegociacoesItemProps } from '../../NegociacoesSalvas.services';
import { StatusTag } from '../StatusTag';
import styles from './TableNegociacoesSalvas.module.scss';

const { Column, HeaderCell, Cell, Pagination } = Table;

type RowDataProps = GetNegociacoesItemProps & {
  pagination?: TablePaginationProps;
  onEditNegotiation?: (id: string) => void;
};

export type TableNegociacoesSalvasProps = TableProps<string, RowDataProps> & {
  /**
   * Se o cabeçalho da coluna de grão deve oferecer ordenação. Vem calculado do gancho da página, que
   * é quem conhece o filtro — a tabela não conhece, e não deve passar a conhecer.
   */
  colunaDeGraoOrdenavel?: boolean;
};

const TruncatedTextWithTooltip = ({ text, maxLength = 50 }: { text: string; maxLength?: number }) => {
  const shouldTruncate = text.length > maxLength;
  const displayText = shouldTruncate ? `${text.slice(0, maxLength)}...` : text;

  if (!shouldTruncate) {
    return <div className={styles['cell-description-text']}>{text}</div>;
  }

  return (
    <Whisper placement="top" trigger="hover" speaker={<Tooltip>{text}</Tooltip>}>
      <div className={classNames(styles['cell-description-text'], styles['truncated-text'])}>{displayText}</div>
    </Whisper>
  );
};

const CellContainer = ({ children, className }: { children: React.ReactNode; className?: string }) => (
  <div className={classNames(styles['table-negociacoes-salvas__cell-content'], className)}>{children}</div>
);

const NegociacaoCell = ({ rowData }: { rowData: RowDataProps }) => (
  <CellContainer>
    <div className={styles['cell-main-text']}>{rowData.name}</div>
    <div className={styles['cell-description-text']}>{`${rowData.totalProducts || 0} ${parseStringToPlural('produto', rowData.totalProducts || 0)}`}</div>
  </CellContainer>
);

const SELO_DE_REDE = 'REDE TODA';

const LojaCell = ({ rowData }: { rowData: RowDataProps }) => {
  // O terceiro ramo, decidido pelo mesmo campo de visão gravada que os outros dois já leem. O texto do
  // selo é o mesmo que o servidor usa para ordenar e para casar a busca desta coluna: divergir faz
  // digitar o que se vê deixar de encontrar a negociação, sem erro em lugar nenhum.
  if (rowData?.visionSaved === 'NETWORK') {
    return (
      <CellContainer>
        {/* A pílula tem contorno e canto redondo próprios, e não o canto de 4px do selo compartilhado:
            é o desenho aprovado no protótipo desta coluna. O ícone é o nó com raios, que é o que
            "rede" desenha. */}
        <TableBadge skin="light-blue" className={styles['selo-de-grao']}>
          <MdHub />
          {SELO_DE_REDE}
        </TableBadge>
      </CellContainer>
    );
  }

  const getDisplayText = () => {
    if (rowData?.visionSaved === 'STORE') {
      return rowData?.stores && rowData.stores.length > 0 ? rowData.stores.join(', ') : '--';
    }
    return rowData?.clusters && rowData?.clusters.length > 0 ? rowData?.clusters.join(', ') : '--';
  };

  const displayText = getDisplayText();

  return (
    <CellContainer>
      {displayText === '--' ? <div className={styles['cell-description-text']}>--</div> : <TruncatedTextWithTooltip text={displayText} maxLength={25} />}
    </CellContainer>
  );
};

const FornecedorCell = ({ rowData }: { rowData: RowDataProps }) => (
  <CellContainer>
    <div className={styles['cell-description-text']}>{rowData.supplierName}</div>
  </CellContainer>
);

const DateCell = ({ date }: { date?: string | null }) => <CellContainer>{date ? formatDateToPtBr(date) : '--'}</CellContainer>;

const ResponsavelCell = ({ owner }: { owner: string }) => <CellContainer>{owner}</CellContainer>;

const StatusCell = ({ status }: { status: 'ONGOING' | 'CLOSED' }) => (
  <CellContainer className={styles['table-negociacoes-salvas__status']}>
    <StatusTag className={styles['table-negociacoes-salvas__status__tag']} status={status} />
  </CellContainer>
);

const ActionCell = ({ rowData, onEditNegotiation }: { rowData: RowDataProps; onEditNegotiation?: (id: string) => void }) => (
  <CellContainer className={styles['table-negociacoes-salvas__status']}>
    <SecondaryButton
      className={styles['table-negociacoes-salvas__button']}
      icon={<RIcon icon="pencil" />}
      onClick={() => onEditNegotiation?.(rowData.id || '')}
    />
  </CellContainer>
);

const TABLE_CONFIG = {
  rowHeight: 64,
  headerHeight: 46,
  cellHeight: 60,
} as const;

export const TableNegociacoesSalvas = ({ pagination, onEditNegotiation, colunaDeGraoOrdenavel = true, ...props }: TableNegociacoesSalvasProps) => (
  <div className={styles['table-negociacoes-salvas']}>
    <Table rowHeight={TABLE_CONFIG.rowHeight} headerHeight={TABLE_CONFIG.headerHeight} autoHeight {...props}>
      {/* @ts-ignore */}
      <Column sortable minWidth={180} flexGrow={1}>
        <HeaderCell>Negociação</HeaderCell>
        <Cell dataKey="name" height={TABLE_CONFIG.cellHeight}>
          {(rowData: RowDataProps) => <NegociacaoCell rowData={rowData} />}
        </Cell>
      </Column>

      {/* @ts-ignore */}
      <Column sortable={colunaDeGraoOrdenavel} width={200}>
        <HeaderCell>Loja</HeaderCell>
        <Cell dataKey="stores" height={TABLE_CONFIG.cellHeight}>
          {(rowData: RowDataProps) => <LojaCell rowData={rowData} />}
        </Cell>
      </Column>

      {/* @ts-ignore */}
      <Column sortable width={180}>
        <HeaderCell>Fornecedor</HeaderCell>
        <Cell dataKey="supplier" height={TABLE_CONFIG.cellHeight}>
          {(rowData: RowDataProps) => <FornecedorCell rowData={rowData} />}
        </Cell>
      </Column>

      {/* @ts-ignore */}
      <Column sortable width={120}>
        <HeaderCell>Início vigência</HeaderCell>
        <Cell dataKey="startDate" height={TABLE_CONFIG.cellHeight}>
          {(rowData: RowDataProps) => <DateCell date={rowData.startDate} />}
        </Cell>
      </Column>

      {/* @ts-ignore */}
      <Column sortable width={120}>
        <HeaderCell>Final vigência</HeaderCell>
        <Cell dataKey="endDate" height={TABLE_CONFIG.cellHeight}>
          {(rowData: RowDataProps) => <DateCell date={rowData.endDate} />}
        </Cell>
      </Column>

      {/* @ts-ignore */}
      <Column sortable width={120}>
        <HeaderCell>Última edição</HeaderCell>
        <Cell dataKey="lastEditedDate" height={TABLE_CONFIG.cellHeight}>
          {(rowData: RowDataProps) => <DateCell date={rowData.lastEditedDate} />}
        </Cell>
      </Column>

      {/* @ts-ignore */}
      <Column sortable width={160}>
        <HeaderCell>Responsável</HeaderCell>
        <Cell dataKey="owner" height={TABLE_CONFIG.cellHeight}>
          {(rowData: RowDataProps) => <ResponsavelCell owner={rowData.owner || '--'} />}
        </Cell>
      </Column>

      {/* @ts-ignore */}
      <Column sortable width={160}>
        <HeaderCell>Status</HeaderCell>
        <Cell dataKey="status" height={TABLE_CONFIG.cellHeight}>
          {(rowData: RowDataProps) => <StatusCell status={rowData.status as 'ONGOING' | 'CLOSED'} />}
        </Cell>
      </Column>

      {/* @ts-ignore */}
      <Column width={120}>
        <HeaderCell> </HeaderCell>
        <Cell dataKey="edit" height={TABLE_CONFIG.cellHeight}>
          {(rowData: RowDataProps) => <ActionCell rowData={rowData} onEditNegotiation={onEditNegotiation} />}
        </Cell>
      </Column>
    </Table>
    <Pagination {...pagination} />
  </div>
);
